import { type FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogContent, DialogActions, Typography, Box } from '@mui/material'
import { useTheme, Button, type ButtonProps } from '@dhl/theme'

import { Dialog, type DialogProps } from './Dialog.js'

export type ConfirmationDialogProps = {
  titleText: string
  acceptText?: string
  rejectText?: string
  open: boolean
  isAcceptButtonDisabled?: boolean
  isAccepting?: boolean
  loadingPosition?: ButtonProps['loadingPosition']
  onAccept: () => void | Promise<void>
} & Omit<DialogProps, 'title'>

/**
 * Standard DHL Confirmation dialog
 * By default it will have default translations but you are able to ask your own confirmation
 * There are handlers in which you can have influence when user is closing, accepting or rejecting the confirmation
 */
export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  titleText,
  acceptText,
  rejectText,
  open,
  testId,
  children,
  isAccepting,
  isAcceptButtonDisabled,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  loadingPosition = 'start',
  onAccept,
  onBackdropClick,
  onClose,
  onEscapeKeyDown,
  onOpen,
  onReject,
}) => {
  const { t } = useTranslation()
  const {
    device: { isMobile },
  } = useTheme()

  const labelReject = rejectText || t('components.confirmationDialog.reject') || 'Annuleren'
  const labelProceed = acceptText || t('components.confirmationDialog.proceed') || 'Ok'

  useEffect(() => {
    if (open) {
      onOpen?.()
    }
  }, [onOpen, open])

  const handleAccept = () => {
    onAccept()
    onClose?.()
  }

  const handleReject = () => {
    onReject?.()
    onClose?.()
  }

  return (
    <Dialog
      open={open}
      title={titleText}
      testId={testId}
      disableBackdropClick={disableBackdropClick}
      disableEscapeKeyDown={disableEscapeKeyDown}
      onReject={handleReject}
      onOpen={onOpen}
      onClose={onClose}
      onBackdropClick={onBackdropClick}
      onEscapeKeyDown={onEscapeKeyDown}
    >
      {children && (
        <DialogContent sx={{ padding: (theme) => ({ xs: theme.spacing(1, 2), sm: 3 }) }}>
          <Typography component="div" variant={isMobile ? 'body2' : 'body1'}>
            {children}
          </Typography>
        </DialogContent>
      )}
      <DialogActions sx={{ pb: 3, pr: { sm: 3 } }}>
        <Box
          display="flex"
          gap={1}
          width="100%"
          justifyContent="flex-end"
          sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
        >
          <Button
            variant="text"
            onClick={handleReject}
            color="black"
            // please use `data-testid`
            data-test="reject"
            data-testid="confirmation-reject"
            disabled={isAccepting}
            size={isMobile ? 'large' : 'medium'}
            sx={{ order: { xs: 2, sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
          >
            {labelReject}
          </Button>

          <Button
            variant="contained"
            onClick={handleAccept}
            // please use `data-testid`
            data-test="accept"
            data-testid="confirmation-accept"
            disabled={isAcceptButtonDisabled}
            fullWidth={isMobile}
            size={isMobile ? 'large' : 'medium'}
            sx={{ order: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}
            loading={isAccepting}
            loadingPosition={loadingPosition}
          >
            {labelProceed}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
